import React, { useMemo } from "react";
import moment from "moment";
import {
  ChartsContainer,
  ChartsXAxis,
  ChartsYAxis,
  ChartsTooltip,
  ChartsLegend,
} from "@mui/x-charts";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { Series, currencyFormatterNull } from "../../../components/Charts";
import { EmptyStateText, TrendChartFill } from "../styles";
import { BREAKPOINTS, GRAPH_COLORS } from "../../../utils/constants";
import { formatCompactNumber } from "../../../utils";
import {
  calculateTickInterval,
  getResponsiveFontSize,
} from "../../../components/Charts/utils";
import { useResponsive } from "../../../hooks/useResponsive";
import { useContainerSize } from "../../../hooks/useContainerSize";

interface TrendChartContentProps {
  incomeTrendSeries: Series[];
  incomeTrendLabels: string[];
}

export const TrendChartContent: React.FC<TrendChartContentProps> = React.memo(
  ({ incomeTrendSeries, incomeTrendLabels }): React.ReactElement => {
    const [containerRef, { width: containerWidth, height: containerHeight }] =
      useContainerSize();
    const isMobile = useResponsive(BREAKPOINTS.mobile);
    const baseHeight = isMobile ? 350 : 400;
    // Fill the card's available height so the chart stretches with the row instead of leaving a bottom gap.
    const height = containerHeight > 0 ? containerHeight : baseHeight;

    const { incomeData, negExpensesData, surplusData } = useMemo(() => {
      const incomeData = incomeTrendSeries[0]?.data ?? [];
      const expensesData = incomeTrendSeries[1]?.data ?? [];
      const negExpensesData = expensesData.map((v) => (v === null ? null : -v));
      const surplusData = incomeData.map((inc, i) => {
        const exp = expensesData[i];
        if (inc === null || exp === null) return null;
        return inc - exp;
      });
      return { incomeData, negExpensesData, surplusData };
    }, [incomeTrendSeries]);

    const { isYearGranularity, formattedLabels } = useMemo(() => {
      const isYearGranularity =
        incomeTrendLabels.length > 0 && /^\d{4}$/.test(incomeTrendLabels[0]);
      const formattedLabels = isYearGranularity
        ? incomeTrendLabels
        : incomeTrendLabels.map((l) => {
            const m = moment(l, "YYYY-MM", true);
            return m.isValid() ? m.format("MMM YYYY") : l;
          });
      return { isYearGranularity, formattedLabels };
    }, [incomeTrendLabels]);

    if (incomeTrendLabels.length === 0 || incomeTrendSeries.length === 0) {
      return <EmptyStateText>No trend data available</EmptyStateText>;
    }

    const step = isYearGranularity
      ? 1
      : calculateTickInterval(incomeTrendLabels.length, isMobile);
    const fontSize = getResponsiveFontSize(isMobile, 12);

    const chartSeries = [
      {
        type: "bar" as const,
        data: incomeData,
        label: "Income",
        color: GRAPH_COLORS[3],
        valueFormatter: currencyFormatterNull,
      },
      {
        type: "bar" as const,
        data: negExpensesData,
        label: "Expenses",
        color: GRAPH_COLORS[8],
        valueFormatter: (v: number | null) =>
          v === null ? "" : currencyFormatterNull(-v),
      },
      {
        type: "line" as const,
        data: surplusData,
        label: "Surplus",
        color: GRAPH_COLORS[4],
        connectNulls: true,
        showMark: false,
        valueFormatter: currencyFormatterNull,
      },
    ];

    return (
      <TrendChartFill ref={containerRef}>
        {containerWidth > 0 && (
          <ChartsContainer
            role="img"
            aria-label="Income, expenses and surplus trend over time"
            series={chartSeries}
            xAxis={[
              {
                scaleType: "band",
                data: formattedLabels,
                tickInterval: (_: unknown, index: number) => index % step === 0,
                tickLabelStyle: {
                  fontFamily: "'Crimson Pro', sans-serif",
                  fontSize,
                  fill: "var(--color-text-default)",
                },
              },
            ]}
            yAxis={[
              {
                valueFormatter: (v: number) => formatCompactNumber(v),
                tickLabelStyle: {
                  fontFamily: "'Crimson Pro', sans-serif",
                  fontSize,
                  fill: "var(--color-text-default)",
                },
              },
            ]}
            width={containerWidth}
            height={height}
            margin={
              isMobile
                ? { left: 8, right: 8, top: 16, bottom: 8 }
                : { left: 16, right: 16, top: 16, bottom: 16 }
            }
            sx={{
              "& .MuiChartsAxis-tickLabel": {
                fontFamily: "'Crimson Pro', sans-serif",
                fill: "var(--color-text-default) !important",
                fontSize: `${fontSize}px`,
              },
              "& .MuiChartsAxis-line": {
                stroke: "var(--color-text-default) !important",
                strokeWidth: 1.5,
              },
              "& .MuiChartsAxis-tick": {
                stroke: "var(--color-text-default) !important",
                strokeWidth: 1.5,
              },
              "& .MuiChartsLegend-label": {
                fontFamily: "'Crimson Pro', sans-serif",
                fill: "var(--color-text-default) !important",
                fontSize: isMobile ? "var(--text-base)" : "var(--text-md)",
              },
              overflow: "visible",
            }}
          >
            <BarPlot />
            <LinePlot />
            <ChartsXAxis />
            <ChartsYAxis />
            <ChartsTooltip />
            <ChartsLegend />
          </ChartsContainer>
        )}
      </TrendChartFill>
    );
  },
);

TrendChartContent.displayName = "TrendChartContent";
